/* eslint-disable */
/**
 * AktivitetsHeatmapBIPanel.jsx - GitHub-inspireret aktivitetsheatmap
 * Endpoint: GET /api/os/bi/aktivitetsheatmap
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const UGEDAGE = ['Son', 'Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lor'];

function heatmapFarve(antal, maxAntal) {
  if (!antal || antal === 0) return '#0f172a';
  const intensity = Math.min(1, antal / Math.max(1, maxAntal));
  if (intensity < 0.15) return '#1e3a5f';
  if (intensity < 0.35) return '#1d4ed8';
  if (intensity < 0.60) return '#2563eb';
  if (intensity < 0.80) return '#3b82f6';
  return '#60a5fa';
}

function fmtDato(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('da-DK', { day: 'numeric', month: 'short' });
}

const TimeTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <div style={{ background: 'rgba(8,12,30,0.95)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '8px 12px', fontSize: 13 }}>
      <div style={{ color: '#7a8fa6' }}>Kl. {d.time}:00</div>
      <div style={{ color: '#3b82f6', fontWeight: 700 }}>{d.antal} sessioner</div>
    </div>
  );
};

export default function AktivitetsHeatmapBIPanel() {
  const [data, setData] = useState(null);
  const [fejl, setFejl] = useState(null);
  const [loader, setLoader] = useState(true);
  const [tooltip, setTooltip] = useState(null);

  const hent = useCallback(async () => {
    try {
      const r = await fetch('/api/os/bi/aktivitetsheatmap', { credentials: 'include' });
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const d = await r.json();
      setData(d);
      setFejl(null);
    } catch (err) {
      setFejl(err.message);
    } finally {
      setLoader(false);
    }
  }, []);

  useEffect(() => { hent(); }, [hent]);

  const gridData = React.useMemo(() => {
    if (!data || !data.heatmap) return { grid: {}, uger: [], maxAntal: 1 };

    const grid = {};
    let maxAntal = 1;

    data.heatmap.forEach(r => {
      const key = r.uge + '-' + r.ugedag;
      grid[key] = { antal: r.antal, dag: r.dag };
      if (r.antal > maxAntal) maxAntal = r.antal;
    });

    const ugerSet = new Set(data.heatmap.map(r => r.uge));
    const uger = Array.from(ugerSet).sort((a, b) => a - b);
    return { grid, uger, maxAntal };
  }, [data]);

  const { grid, uger, maxAntal } = gridData;

  const timeData = React.useMemo(() => {
    if (!data || !data.time_of_day) return [];
    const arr = Array.from({ length: 24 }, (_, i) => ({ time: i, antal: 0 }));
    data.time_of_day.forEach(r => {
      if (r.time >= 0 && r.time < 24) arr[r.time].antal = r.antal;
    });
    return arr;
  }, [data]);

  const CELLE_SIZE = 12;
  const CELLE_GAP = 2;

  return (
    <div style={{ width: '100%' }}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#e2e8f0' }}>Aktivitetsheatmap</div>
        <div style={{ fontSize: 13, color: '#7a8fa6', marginTop: 3 }}>
          Haendelser per dag (52 uger) og beregner-aktivitet per time
        </div>
      </div>

      {loader && <div style={{ textAlign: 'center', padding: 40, color: '#7a8fa6' }}>Henter...</div>}
      {fejl && !loader && (
        <div style={{ background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, padding: '12px 16px', color: '#f87171', fontSize: 13 }}>
          Fejl: {fejl}
        </div>
      )}

      {!loader && !fejl && data && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#9db3c8', marginBottom: 10 }}>
              Haendelser per dag (52 uger)
            </div>
            <div style={{ overflowX: 'auto', width: '100%', WebkitOverflowScrolling: 'touch' }}>
              <div style={{ display: 'flex', gap: 0, minWidth: Math.max(320, uger.length * (CELLE_SIZE + CELLE_GAP) + 44) }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: CELLE_GAP, marginRight: 4 }}>
                  {UGEDAGE.map((dag, i) => (
                    <div key={i} style={{
                      height: CELLE_SIZE, fontSize: 9, color: '#7a8fa6',
                      display: 'flex', alignItems: 'center',
                    }}>
                      {i % 2 === 1 ? dag : ''}
                    </div>
                  ))}
                </div>
                <svg
                  width={uger.length * (CELLE_SIZE + CELLE_GAP)}
                  height={7 * (CELLE_SIZE + CELLE_GAP)}
                >
                  {uger.map((uge, ugeidx) =>
                    UGEDAGE.map((_, ugedag) => {
                      const key = uge + '-' + ugedag;
                      const cell = grid[key];
                      const x = ugeidx * (CELLE_SIZE + CELLE_GAP);
                      const y = ugedag * (CELLE_SIZE + CELLE_GAP);
                      const farve = cell ? heatmapFarve(cell.antal, maxAntal) : '#0f172a';
                      return (
                        <rect
                          key={key}
                          x={x} y={y}
                          width={CELLE_SIZE} height={CELLE_SIZE}
                          rx={2} ry={2}
                          fill={farve}
                          onMouseEnter={(e) => {
                            if (cell) {
                              setTooltip({ x: e.clientX, y: e.clientY, dato: fmtDato(cell.dag), antal: cell.antal });
                            }
                          }}
                          onMouseLeave={() => setTooltip(null)}
                        />
                      );
                    })
                  )}
                </svg>
              </div>
            </div>

            {tooltip && (
              <div style={{
                position: 'fixed', left: tooltip.x + 12, top: tooltip.y - 8,
                background: 'rgba(8,12,30,0.95)', border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 7, padding: '6px 10px', fontSize: 12, zIndex: 9999,
                pointerEvents: 'none', color: '#e2e8f0',
              }}>
                <div style={{ color: '#7a8fa6' }}>{tooltip.dato}</div>
                <div style={{ fontWeight: 700, color: '#3b82f6' }}>{tooltip.antal} haendelser</div>
              </div>
            )}

            <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, color: '#7a8fa6', flexWrap: 'wrap' }}>
              <span>Ingen</span>
              {[0, 0.25, 0.5, 0.75, 1.0].map((v, i) => (
                <div key={i} style={{
                  width: 10, height: 10, borderRadius: 2,
                  background: v === 0 ? '#0f172a' : heatmapFarve(Math.round(v * maxAntal), maxAntal),
                }} />
              ))}
              <span>Mange ({maxAntal})</span>
            </div>
          </div>

          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#9db3c8', marginBottom: 10 }}>
              Beregner-aktivitet per time pa dagen
            </div>
            {timeData.every(d => d.antal === 0) ? (
              <div style={{ color: '#7a8fa6', fontSize: 13 }}>Ingen beregner-data endnu</div>
            ) : (
              <div style={{ width: '100%', overflowX: 'auto' }}>
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={timeData} margin={{ top: 0, right: 8, bottom: 0, left: -10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                    <XAxis
                      dataKey="time"
                      tickFormatter={function(v) { return v + ':00'; }}
                      tick={{ fill: '#7a8fa6', fontSize: 9 }}
                      axisLine={{ stroke: 'rgba(255,255,255,0.08)' }}
                      tickLine={false}
                      interval={3}
                    />
                    <YAxis
                      tick={{ fill: '#7a8fa6', fontSize: 10 }}
                      axisLine={false}
                      tickLine={false}
                      width={28}
                    />
                    <Tooltip content={<TimeTooltip />} />
                    <Bar dataKey="antal" fill="#1d4ed8" radius={[2, 2, 0, 0]} maxBarSize={20} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
